import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import './BrowseNeeds.css';
import { useCart } from '../../context/CartContext';
import { trackEvent } from "../../utils/analytics";
import { CFR_CATEGORIES } from "../../constants/cfrCategories";



const ORG_TYPES = [
  { value: 'all', label: 'All causes' },
  { value: 'homeless_shelter', label: 'Homeless shelter' },
  { value: 'youth_center', label: 'Youth center' },
  { value: 'diaper_bank', label: 'Diaper bank' },
  { value: 'domestic_violence', label: 'Domestic violence' },
  { value: 'transitional_housing', label: 'Transitional housing' },
  { value: 'food_pantry', label: 'Food pantry' },
  { value: 'family_services', label: 'Family services' },
  { value: 'other', label: 'Other nonprofit' }
];

function BrowseNeeds({ needType = 'supply' }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [needs, setNeeds] = useState([]);
  const [orgs, setOrgs] = useState({});
  const [loading, setLoading] = useState(true);
  const [priority, setPriority] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [frequency, setFrequency] = useState('all');
  const [orgType, setOrgType] = useState('all');
  const [cfrFilter, setCfrFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [selectedNeedType, setSelectedNeedType] = useState(needType);
  const { addToCart, removeFromCart, isInCart, cartItems } = useCart();

  useEffect(() => { fetchData(); }, [selectedNeedType]);

  async function fetchData() {
    try {
      const [needsSnap, orgsSnap] = await Promise.all([
        getDocs(query(collection(db, 'needs'), where('status', '==', 'open'))),
        getDocs(query(collection(db, 'organizations'), where('status', '==', 'active')))
      ]);

      const visibleNeeds = needsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(n => n.isParent !== true)
        .filter(n => (n.type || 'supply') === selectedNeedType);

      setNeeds(visibleNeeds);

      const orgMap = {};
      orgsSnap.docs.forEach(d => { orgMap[d.id] = d.data(); });
      setOrgs(orgMap);
    } catch (err) {
      console.error('Error fetching needs:', err);
    } finally {
      setLoading(false);
    }
  }

  function getFiltered() {
    let result = [...needs];

    if (priority !== 'all') result = result.filter(n => n.priority === priority);

    if (priceRange !== 'all') {
      result = result.filter(n => {
        const p = n.pricePerCycle || 0;
        if (priceRange === 'under15') return p < 15;
        if (priceRange === '15to30') return p >= 15 && p <= 30;
        if (priceRange === '30to50') return p > 30 && p <= 50;
        if (priceRange === 'over50') return p > 50;
        return true;
      });
    }

    if (frequency !== 'all') result = result.filter(n => n.frequency === frequency);

    if (orgType !== 'all') {
      result = result.filter(n => {
        const org = orgs[n.orgId];
        return org && org.orgType === orgType;
      });
    }

    if (cfrFilter !== 'all') {
      result = result.filter(n => (n.cfrCategories || []).includes(cfrFilter));
    }

    const priorityOrder = { critical: 1, core: 2, supplemental: 3 };
    result.sort((a, b) => {
      if (sortBy === 'priority') {
        const pDiff = (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
        if (pDiff !== 0) return pDiff;
        return (a.pricePerCycle || 999) - (b.pricePerCycle || 999);
      }
      if (sortBy === 'price_low') return (a.pricePerCycle || 0) - (b.pricePerCycle || 0);
      if (sortBy === 'price_high') return (b.pricePerCycle || 0) - (a.pricePerCycle || 0);
      if (sortBy === 'newest') return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      return 0;
    });

    return result;
  }

  const isSponsorshipPage = selectedNeedType === 'sponsorship';
  const pageTitle = isSponsorshipPage ? 'Open sponsorships in Springfield' : 'Open needs in Springfield';
  const pageDescription = isSponsorshipPage
    ? 'These are sponsorship opportunities from vetted local nonprofits. SomebodyGives helps make the introduction; the nonprofit handles sponsorship details directly.'
    : 'Every need below is from a vetted local nonprofit. Adopt one and it ships automatically on schedule.';
  const loadingText = isSponsorshipPage ? 'Loading open sponsorships...' : 'Loading open needs...';
  const resultLabel = isSponsorshipPage ? 'sponsorship' : 'need';
  const filtered = getFiltered();
  const activeFilterCount = [
    priority !== 'all', priceRange !== 'all',
    frequency !== 'all', orgType !== 'all', cfrFilter !== 'all'
  ].filter(Boolean).length;

  function resetFilters() {
    setPriority('all'); setPriceRange('all');
    setFrequency('all'); setOrgType('all');
    setCfrFilter('all'); setSortBy('priority');
  }

  return (
    <div className="dashboard">

      <header className="dashboard-header">
        <div className="logo" onClick={() => navigate('/')}>somebody.gives</div>
        <nav className="dashboard-nav">
          <span className="nav-link" onClick={() => navigate('/give/dashboard')}>My commitments</span>
          <span className={`nav-link ${!isSponsorshipPage ? 'active' : ''}`} onClick={() => navigate('/give/browse')}>Browse needs</span>
          <span className={`nav-link ${isSponsorshipPage ? 'active' : ''}`} onClick={() => navigate('/give/sponsorships')}>Browse sponsorships</span>
        </nav>
        <div className="dashboard-user" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span className="user-name">{userProfile?.firstName || 'Donor'}</span>
          {cartItems.length > 0 && (
            <button onClick={() => navigate('/give/cart')} style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '0.4rem 0.875rem', borderRadius: '20px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
              🛒 Cart ({cartItems.length})
            </button>
          )}
        </div>
      </header>

      <main className="dashboard-main">

        <div className="browse-header">
  <h1>{pageTitle}</h1>
  <p>{pageDescription}</p>

  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
    <button
      onClick={() => setSelectedNeedType('supply')}
      style={{
        padding: '0.5rem 1rem',
        borderRadius: '999px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 700,
        background: selectedNeedType === 'supply' ? '#1e3a5f' : '#e5e7eb',
        color: selectedNeedType === 'supply' ? 'white' : '#111827'
      }}
    >
      Supply Needs
    </button>

    <button
      onClick={() => setSelectedNeedType('sponsorship')}
      style={{
        padding: '0.5rem 1rem',
        borderRadius: '999px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 700,
        background: selectedNeedType === 'sponsorship' ? '#1e3a5f' : '#e5e7eb',
        color: selectedNeedType === 'sponsorship' ? 'white' : '#111827'
      }}
    >
      Sponsorships
    </button>
  </div>
</div>

        {/* Filter bar */}
        <div className="filter-panel">
          <div className="filter-row">
            <span className="filter-label">Priority:</span>
            {['all', 'critical', 'core', 'supplemental'].map(f => (
              <button key={f} className={`filter-btn ${priority === f ? 'active' : ''}`} onClick={() => setPriority(f)}>
                {f === 'all' ? 'All' : f}
              </button>
            ))}
          </div>

          <div className="filter-row">
            <span className="filter-label">Price:</span>
            {[
              { value: 'all', label: 'Any' },
              { value: 'under15', label: 'Under $15' },
              { value: '15to30', label: '$15-$30' },
              { value: '30to50', label: '$30-$50' },
              { value: 'over50', label: 'Over $50' }
            ].map(f => (
              <button key={f.value} className={`filter-btn ${priceRange === f.value ? 'active' : ''}`} onClick={() => setPriceRange(f.value)}>
                {f.label}
              </button>
            ))}
          </div>

          <div className="filter-row">
            <span className="filter-label">Frequency:</span>
            {[
              { value: 'all', label: 'Any' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'bi-monthly', label: 'Bi-monthly' },
              { value: 'quarterly', label: 'Quarterly' },
              { value: 'one-time', label: 'One-time' }
            ].map(f => (
              <button key={f.value} className={`filter-btn ${frequency === f.value ? 'active' : ''}`} onClick={() => setFrequency(f.value)}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Community Priority (CFR) filter */}
          <div className="filter-row">
            <span className="filter-label">Community Priority (CFR):</span>
            <button className={`filter-btn ${cfrFilter === 'all' ? 'active' : ''}`} onClick={() => setCfrFilter('all')}>All</button>
            {CFR_CATEGORIES.map(cat => (
              <button key={cat.value} className={`filter-btn ${cfrFilter === cat.value ? 'active' : ''}`} onClick={() => setCfrFilter(cat.value)}>
                {cat.label}
              </button>
            ))}
          </div>

          <div className="filter-row">
            <span className="filter-label">Cause:</span>
            <select className="filter-select" value={orgType} onChange={e => setOrgType(e.target.value)}>
              {ORG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>

            <span className="filter-label" style={{ marginLeft: '1rem' }}>Sort:</span>
            <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="priority">Priority first</option>
              <option value="price_low">Price: low to high</option>
              <option value="price_high">Price: high to low</option>
              <option value="newest">Newest first</option>
            </select>

            {activeFilterCount > 0 && (
              <button className="filter-btn filter-clear" onClick={resetFilters}>
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        </div>

        {!loading && (
          <div className="results-count">
            {filtered.length} {resultLabel}{filtered.length !== 1 ? 's' : ''} found
            {activeFilterCount > 0 ? ' matching your filters' : ''}
          </div>
        )}

        {loading && <div className="browse-loading">{loadingText}</div>}

        {!loading && filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h2>No needs match your filters</h2>
            <p>Try adjusting your filters or <span className="reset-link" onClick={resetFilters}>clear all filters</span></p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="needs-grid">
            {filtered.map(need => {
              const needCfr = (need.cfrCategories || [])
                .map(val => CFR_CATEGORIES.find(c => c.value === val))
                .filter(Boolean);

              return (
                <div key={need.id} className="need-card">
                  <div className="need-card-header">
                    <span className={`priority-badge ${need.priority}`}>{need.priority}</span>
                    <span className="need-frequency">{need.frequency}</span>
                  </div>

                  <div style={{ display: 'flex', gap: '0.875rem', alignItems: 'flex-start' }}>
                    {orgs[need.orgId]?.logoUrl ? (
                      <img src={orgs[need.orgId].logoUrl} alt={`${need.orgName} logo`} style={{ width: '52px', height: '52px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0, border: '1px solid #e5e7eb', background: 'white' }} />
                    ) : (
                      <div style={{ width: '52px', height: '52px', borderRadius: '10px', flexShrink: 0, border: '1px solid #e5e7eb', background: '#eef2f7', color: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem' }}>
                        {(need.orgName || '?').charAt(0)}
                      </div>
                    )}
                    <div>
                      <div className="need-org" style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1e3a5f' }} onClick={() => navigate(`/o/${need.orgId}`)}>
                        {need.orgName}
                      </div>
                      <h3 className="need-title">{need.itemName}</h3>
                      <p className="need-impact">{need.impactStatement}</p>
                      {needCfr.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' }}>
                          {needCfr.map(cat => (
                            <span key={cat.value} style={{ padding: '0.15rem 0.55rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: 600, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>
                              {cat.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="need-footer">
                    <div className="need-cost">
                      ${Math.ceil(need.pricePerCycle)}<span>/{need.frequency}</span>
                    </div>
                    {isSponsorshipPage ? (
                      <>
                        <button
  className="btn-adopt"
  onClick={() => {
    trackEvent("click_sponsorship_commitment_start", {
      need_id: need.id,
      org_name: need.orgName,
      price: need.pricePerCycle
    });

    if (
      userProfile?.role === 'org' ||
      userProfile?.role === 'organization' ||
      userProfile?.role === 'admin'
    ) {
      alert('You are signed in as an organization/admin. To sponsor this need, sign in with a donor account.');
      return;
    }

    navigate(`/give/sponsorships/${need.id}/commit`);
  }}
>
  Sponsor this
</button>
                        <div style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '0.35rem', color: '#555' }}>
                          Nonprofit will contact you directly
                        </div>
                      </>
                    ) : (
                      <>
                        <button
                          className={`btn-adopt ${isInCart(need.id) ? 'in-cart' : ''}`}
                          onClick={() => {
                            if (isInCart(need.id)) {
                              removeFromCart(need.id);
                            } else {
                              trackEvent("add_to_cart", { need_id: need.id, org_name: need.orgName, price: need.pricePerCycle });
                              addToCart(need);
                            }
                          }}
                        >
                          {isInCart(need.id) ? '✓ In cart' : 'Take Responsibility'}
                        </button>
                        <div style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '0.35rem' }}>
                          <span style={{ color: '#555', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                              trackEvent("click_take_responsibility", { need_id: need.id, org_name: need.orgName, price: need.pricePerCycle });
                              navigate(`/give/adopt/${need.id}`);
                            }}>
                            Adopt just this one
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </main>
    </div>
  );
}

export default BrowseNeeds;
